import { AxiosError } from 'axios'
import { useMutation, UseMutationOptions, useQueryClient } from 'react-query'
import { useDispatch } from 'react-redux'

import { notificationsApi } from './notifications.api'
import {
  COUNT_NEW_NOTIFICATIONS_KEY,
  NOTIFICATIONS_KEY,
  NOTIFICATIONS_SETTINGS_KEY
} from './notifications.constants'
import {
  NotificationSettingsCategories,
  NotificationSettingsCategoryKey,
  NotificationSettingsCategoryKeys
} from './notifications.types'
import { components } from '../../../generated'
import { pushSuccessNotification } from '../../snackbar'
import { useLocale, useTranslation } from '../../../utils'

export const useMutationReadAllNotification = (
  options?: UseMutationOptions<
    unknown,
    AxiosError,
    components['schemas']['AlertServiceType']
  >
) => {
  const queryClient = useQueryClient()
  const locale = useLocale()

  const mutation = useMutation(
    async (type) => await notificationsApi.readAllNotification(type, locale),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.setQueryData<components['schemas']['AlertResource'][]>(
          [NOTIFICATIONS_KEY, variables, locale],
          (notifications) =>
            (notifications ?? [])?.map((notification) => ({
              ...notification,
              isRead: true
            }))
        )

        queryClient.setQueryData<components['schemas']['AlertsCountResource']>(
          COUNT_NEW_NOTIFICATIONS_KEY,
          (countNewNotification) => ({
            ...countNewNotification,
            count: {
              ...countNewNotification?.count,
              [variables]: 0
            }
          })
        )

        options?.onSuccess?.(data, variables, context)
      }
    }
  )

  return mutation
}

export const useMutationReadNotification = (
  options?: UseMutationOptions<
    unknown,
    AxiosError,
    {
      type?: components['schemas']['AlertServiceType']
      notificationId: number
    }
  >
) => {
  const queryClient = useQueryClient()
  const locale = useLocale()

  const mutation = useMutation(
    async (variables) => {
      if (variables.type) {
        return await notificationsApi.readNotification(
          variables.notificationId,
          variables.type,
          locale
        )
      }
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.setQueriesData<components['schemas']['AlertResource'][]>(
          [NOTIFICATIONS_KEY, locale],
          (notifications) =>
            (notifications ?? [])?.map((notification) =>
              notification.id === variables.notificationId
                ? { ...notification, isRead: true }
                : notification
            )
        )

        queryClient.setQueryData<components['schemas']['AlertsCountResource']>(
          COUNT_NEW_NOTIFICATIONS_KEY,
          (countNewNotification) => {
            if (!variables.type) {
              return { ...countNewNotification }
            }

            const count = countNewNotification?.count?.[variables.type]

            const newCountNotificationService =
              count && count > 0 ? count - 1 : 0

            return {
              ...countNewNotification,
              count: {
                ...countNewNotification?.count,
                [variables.type]: newCountNotificationService
              }
            }
          }
        )

        options?.onSuccess?.(data, variables, context)
      }
    }
  )

  return mutation
}

export const useMutationAddSubscriberLanding = (
  options?: UseMutationOptions<
    unknown,
    AxiosError,
    components['schemas']['AddSubscriberRequest']
  >
) => {
  const dispatch = useDispatch()
  const { data: translate } = useTranslation()
  const locale = useLocale()
  const mutation = useMutation(
    async (requestBody) =>
      await notificationsApi.addSubscriberLanding(requestBody, locale),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        dispatch(
          pushSuccessNotification(
            translate?.['successfully-subscribed-updates']
          )
        )
        options?.onSuccess?.(data, variables, context)
      }
    }
  )

  return mutation
}

export const useMutationSubscribeTaskNotifications = (
  options?: UseMutationOptions<unknown, AxiosError, number>
) => {
  const dispatch = useDispatch()
  const { data: translate } = useTranslation()
  const locale = useLocale()
  const mutation = useMutation(
    async (taskId) =>
      await notificationsApi.subscribeTaskNotifications(taskId, locale),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        dispatch(
          pushSuccessNotification(
            translate?.['successfully-subscribed-updates']
          )
        )
        options?.onSuccess?.(data, variables, context)
      }
    }
  )

  return mutation
}

export const useMutationSubscribeTeamTaskNotifications = (
  options?: UseMutationOptions<unknown, AxiosError, number>
) => {
  const dispatch = useDispatch()
  const { data: translate } = useTranslation()
  const locale = useLocale()
  const mutation = useMutation(
    async (taskId) =>
      await notificationsApi.subscribeTeamTaskNotifications(taskId, locale),

    {
      ...options,
      onSuccess: (data, variables, context) => {
        dispatch(
          pushSuccessNotification(
            translate?.['team-successfully-subscribed-notifications']
          )
        )
        options?.onSuccess?.(data, variables, context)
      }
    }
  )

  return mutation
}

export const useMutationSetNotificationSettings = (
  type: components['schemas']['AlertServiceType'],
  options?: UseMutationOptions<
    unknown,
    AxiosError,
    components['schemas']['SaveAllSettingsRequest']
  >
) => {
  const queryClient = useQueryClient()
  const locale = useLocale()

  const mutation = useMutation(
    async (requestBody) =>
      await notificationsApi.setNotificationSettings(type, requestBody, locale),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries([
          NOTIFICATIONS_SETTINGS_KEY,
          type,
          locale
        ])

        options?.onSuccess?.(data, variables, context)
      }
    }
  )

  return mutation
}

export const useMutationSaveNotificationsSettings = (
  type: components['schemas']['AlertServiceType'],
  options?: UseMutationOptions<
    unknown,
    AxiosError,
    components['schemas']['SaveSettingsRequest']
  >
) => {
  const queryClient = useQueryClient()
  const locale = useLocale()

  const mutation = useMutation(
    async (requestBody) =>
      await notificationsApi.saveNotificationsSettings(
        type,
        requestBody,
        locale
      ),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.setQueryData<NotificationSettingsCategories>(
          [NOTIFICATIONS_SETTINGS_KEY, type, locale],
          (notificationsSettings) => {
            const group = variables.group as NotificationSettingsCategoryKeys
            const option =
              variables.option as keyof NotificationSettingsCategoryKey
            const value =
              variables.value as components['schemas']['AlertSettingsValues']

            return {
              ...notificationsSettings,
              ...(group
                ? {
                    [group]: {
                      ...notificationsSettings?.[group],
                      ...(option
                        ? {
                            [option]: value
                          }
                        : {})
                    }
                  }
                : {})
            }
          }
        )

        options?.onSuccess?.(data, variables, context)
      }
    }
  )

  return mutation
}
